import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, ArrowRight, Sparkles, Globe2, PenSquare, Plus, Check } from "lucide-react";

/**
 * Recommended drills for a player.
 *
 * Given the player's current main-skill ratings we pick the 2 lowest
 * (excluding zero/unset) as the target sub-skills, then query the drill
 * library for drills that:
 *   - match the sport
 *   - contain any of those weakest sub-skills in their tags
 *   - fall within a ±1.0 window around the player's current level
 *
 * De-duplicated by drill id, capped at 6, sorted rally_lab-first so
 * coaches see official material before community shares.
 */

const SOURCE_META = {
  rally_lab: { label: "Rally Lab", color: "#CCFF00", icon: Sparkles },
  coach_shared: { label: "Shared", color: "#00BFFF", icon: Globe2 },
  coach_draft: { label: "Draft", color: "#F59E0B", icon: PenSquare },
};

export default function RecommendedDrills({ sport, level, ratings, studentId, currentFocus, onFocusAppended }) {
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinning, setPinning] = useState(null); // drill.id currently being posted
  const [pinned, setPinned] = useState({});     // drill.id -> true once snapshotted

  // Pre-seed `pinned` from the persisted session focus so drills already in
  // the plan show 'IN PLAN' on reload. We match the bullet prefix pattern
  // written by the backend: "• {title} (" (see server.py:append_drill_to_focus).
  useEffect(() => {
    if (!currentFocus || drills.length === 0) return;
    const next = {};
    for (const d of drills) {
      const marker = `• ${d.title} (`;
      if (currentFocus.includes(marker)) next[d.id] = true;
    }
    setPinned(next);
  }, [currentFocus, drills]);

  // Two weakest main skills (out of what the coach has actually rated).
  const weakest = useMemo(() => {
    const entries = Object.entries(ratings || {})
      .filter(([, v]) => v && Number.isFinite(v))
      .sort((a, b) => a[1] - b[1]);
    return entries.slice(0, 2).map(([k]) => k);
  }, [ratings]);

  // Level window: level ±1.0, floored to 2.0, capped at 5.0.
  const levelWindow = useMemo(() => {
    const lv = parseFloat(level) || 3.0;
    return { min: Math.max(2.0, lv - 1.0), max: Math.min(5.0, lv + 1.0) };
  }, [level]);

  useEffect(() => {
    if (!sport || weakest.length === 0) { setDrills([]); return; }
    setLoading(true);
    // One request per weakest skill, then dedupe. Small N so the fan-out is fine.
    Promise.all(weakest.map((sk) =>
      api.get("/drills", { params: {
        sport, sub_skill: sk,
        min_level: levelWindow.min, max_level: levelWindow.max,
      }}).then((r) => r.data || []).catch(() => [])
    )).then((results) => {
      const seen = new Set();
      const merged = [];
      // Interleave results so both weak skills get representation instead of
      // one skill's drills dominating the list.
      let more = true;
      let i = 0;
      while (more) {
        more = false;
        for (const list of results) {
          if (list[i] && !seen.has(list[i].id)) {
            seen.add(list[i].id);
            merged.push(list[i]);
            more = true;
          }
        }
        i += 1;
        if (merged.length >= 6) break;
      }
      // rally_lab first, then coach_shared, then coach_draft
      merged.sort((a, b) => {
        const rank = (s) => s === "rally_lab" ? 0 : s === "coach_shared" ? 1 : 2;
        return rank(a.source) - rank(b.source);
      });
      setDrills(merged.slice(0, 6));
    }).finally(() => setLoading(false));
  }, [sport, weakest.join("|"), levelWindow.min, levelWindow.max]);

  if (weakest.length === 0) return null;

  const snapshotToPlan = async (drill, ev) => {
    // Called from an inner button; the card itself is a <Link>, so stop the
    // click from bubbling into a navigation to /drills/:id.
    ev.preventDefault();
    ev.stopPropagation();
    if (!studentId || pinning === drill.id) return;
    setPinning(drill.id);
    try {
      const res = await api.post(`/focus/${studentId}/append-drill`, {
        sport, drill_id: drill.id, duration_min: drill.duration_min,
      });
      setPinned((p) => ({ ...p, [drill.id]: true }));
      if (res.data?.skipped) {
        toast.info(`${drill.title} is already in the session plan`);
      } else {
        toast.success(`Added to session plan: ${drill.title}`);
      }
      onFocusAppended?.(res.data?.focus || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add drill to session plan");
    } finally {
      setPinning(null);
    }
  };

  return (
    <div className="card-surface p-6 fade-up d3" data-testid="recommended-drills">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono">Recommended drills</div>
        </div>
        <Link
          to={`/drills?sport=${sport}&sub_skill=${encodeURIComponent(weakest[0])}`}
          className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1"
          data-testid="see-more-drills"
        >
          Full library <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <p className="text-xs text-[#A3A3A3] mb-3">
        Targeted at the two weakest skills we&apos;ve rated for this player
        (<span className="text-white">{weakest.join(", ")}</span>) in the L{levelWindow.min.toFixed(1)}–{levelWindow.max.toFixed(1)} range.
      </p>

      {loading && <div className="text-xs text-[#A3A3A3] italic" data-testid="recs-loading">Loading…</div>}
      {!loading && drills.length === 0 && (
        <div className="text-xs text-[#A3A3A3] italic" data-testid="recs-empty">
          No matching drills in the library yet. Try adding your own via <Link to="/drills" className="text-[#CCFF00] underline">Drill Library</Link>.
        </div>
      )}
      {!loading && drills.length > 0 && (
        <div className="grid md:grid-cols-2 gap-2" data-testid="recs-list">
          {drills.map((d) => {
            const meta = SOURCE_META[d.source] || SOURCE_META.coach_draft;
            const Icon = meta.icon;
            const isPinned = !!pinned[d.id];
            const isPinning = pinning === d.id;
            return (
              <Link
                key={d.id}
                to={`/drills/${d.id}`}
                className="border border-[#262626] hover:border-[#CCFF00] p-3 transition-colors"
                data-testid={`rec-drill-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-sm text-white line-clamp-2 flex-1 min-w-0">{d.title}</div>
                  <span className="text-[9px] uppercase tracking-wider label-mono px-1 py-0.5 shrink-0" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1 }}>
                    <Icon className="w-2.5 h-2.5 inline mr-0.5" />
                    {meta.label}
                  </span>
                </div>
                {d.summary && <div className="text-[10px] text-[#A3A3A3] line-clamp-2">{d.summary}</div>}
                <div className="flex items-center justify-between gap-2 mt-1">
                  <div className="text-[9px] text-[#A3A3A3] label-mono">
                    L{d.skill_level_min}-{d.skill_level_max} · {d.duration_min}m · {(d.sub_skills || []).slice(0, 3).join(", ")}
                  </div>
                  {studentId && (
                    <button
                      type="button"
                      onClick={(ev) => snapshotToPlan(d, ev)}
                      disabled={isPinning || isPinned}
                      className={`text-[9px] uppercase tracking-wider label-mono px-1.5 py-0.5 border shrink-0 inline-flex items-center gap-1 transition-colors ${
                        isPinned
                          ? "text-[#CCFF00] border-[#CCFF00] cursor-default"
                          : "text-[#A3A3A3] border-[#404040] hover:text-[#CCFF00] hover:border-[#CCFF00]"
                      }`}
                      data-testid={`rec-drill-pin-${d.id}`}
                      title={isPinned ? "Added to session plan" : "Add to session plan"}
                    >
                      {isPinned ? (
                        <><Check className="w-2.5 h-2.5" /> In plan</>
                      ) : (
                        <><Plus className="w-2.5 h-2.5" /> {isPinning ? "Adding…" : "Plan"}</>
                      )}
                    </button>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
